import React, { useMemo, useState } from "react";
import { Box, Typography, Link, Divider } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import "./Footer.css";
import { ASSETS_BASE_URL } from "../../config";
import { useTranslation } from "react-i18next";
import Language from "../../Language/language";
import { LABELS, ROUTES, UI_TEXT } from "../../constants";
import { useCompliance } from "../../context/complianceContext";
import ComplianceDescriptionModal from "../CompliancePolicy/ComplianceDescriptionModal";
import EulaLibraryModal from "../CompliancePolicy/EulaLibraryModal";
import { openComplianceDocument } from "../CompliancePolicy/complianceUtils";
import useBrandingAssets from "../../hooks/useBrandingAssets";
import CompanyLogo from "../CompanyLogo/CompanyLogo";
import useCompanyInfo from "../../hooks/useCompanyInfo";
import { useOptions } from "../../context/optionContext";
import { useNavigate } from "react-router-dom";

const Footer = () => {
	const { t } = useTranslation();
	const { items } = useCompliance();
	const navigate = useNavigate();
	const eulaDocs = items.EULA || [];
	const privacyDoc = items.PRIVACY || null;
	const termsDoc = items.TERMS || null;
	const supportDoc = items.SUPPORT || null;
	const cookieDoc = items.COOKIE || null;
	const [policyModal, setPolicyModal] = useState({ open: false, doc: null });
	const [eulaModalOpen, setEulaModalOpen] = useState(false);
	const { footerLogoEnabled } = useBrandingAssets();
	const { companyName, companyEmail, companyAddress, companyPhone } = useCompanyInfo();
	const [markerDownOpen, setMarkerDownOpen] = useState(false);
	const { optionKeys } = useOptions();
	const showLanguageSelector = optionKeys?.enableLanguageTranslation !== "false";
	const showFooterVersion = optionKeys?.footerShowVersion !== "false";
	const showFooterSupport = optionKeys?.footerShowSupport !== "false";
	const showFooterTerms = optionKeys?.footerShowTerms !== "false";
	const showFooterEula = optionKeys?.footerShowEula !== "false";
	const showFooterPrivacy = optionKeys?.footerShowPrivacy !== "false";
	const showFooterManual = optionKeys?.footerShowManual !== "false";
	const showFooterNeedHelp = optionKeys?.footerShowNeedHelp !== "false";
	const showFooterCookiePolicy = optionKeys?.footerShowCookiePolicy !== "false";

	const quickLinks = [
		{ label: t('LABELS.HOME'), path: "/" },
		{ label: t('LABELS.MACHINESS'), path: "/machines" },
		{ label: t('LABELS.RSPL'), path: "/rspl" },
		{ label: t('LABELS.RECOMMENDATION'), path: "/my-lists" },
	];

	const quickActions = [
		{ label: t('LABELS.SEARCH'), path: "/search-results" },
		{ label: t('LABELS.TRACK_ORDER'), path: "/track-order" },
		{ label: t('LABELS.CONTACT_CCR'), path: "/support" },
	];

	const legalLinks = useMemo(
		() => [
			{ id: "support", label: t('LABELS.SUPPORT'), doc: supportDoc, fallback: "https://support.ithena.io/" },
			{ id: "terms", label: t('TITLES.TERMS_AND_CONDITIONS'), doc: termsDoc, fallback: "https://privacy.ithena.io/terms" },
			{ id: "privacy", label: t('LABELS.PRIVACY_POLICY'), doc: privacyDoc, fallback: "https://privacy.ithena.io/" },
			{ id: "cookie", label: t('LABELS.COOKIE_POLICY', { defaultValue: 'Cookie Policy' }), doc: cookieDoc, fallback: "https://privacy.ithena.io/" },
		],
		[supportDoc, termsDoc, privacyDoc, cookieDoc, t]
	);

	const visibleLegalLinks = useMemo(
		() =>
			legalLinks.filter((link) => {
				if (link.id === "support") return showFooterSupport;
				if (link.id === "terms") return showFooterTerms;
				if (link.id === "privacy") return showFooterPrivacy;
				if (link.id === "cookie") return showFooterCookiePolicy;
				return true;
			}),
		[legalLinks, showFooterTerms, showFooterPrivacy, showFooterSupport, showFooterCookiePolicy]
	);

	const handlePolicyClick = (item) => {
		openComplianceDocument({
			document: item.doc,
			fallbackUrl: item.fallback,
			onShowDescription: (document) =>
				setPolicyModal({ open: true, doc: document }),
		});
	};

	const handleEulaClick = () => {
		if (!eulaDocs.length) {
			openComplianceDocument({
				document: null,
				fallbackUrl: "https://eula.ithena.io/",
				onShowDescription: (document) =>
					setPolicyModal({ open: true, doc: document }),
			});
			return;
		}
		if (eulaDocs.length === 1) {
			openComplianceDocument({
				document: eulaDocs[0],
				fallbackUrl: "https://eula.ithena.io/",
				onShowDescription: (document) =>
					setPolicyModal({ open: true, doc: document }),
			});
			return;
		}
		setEulaModalOpen(true);
	};

	const contactInfo = [
		{ icon: <LocationOnIcon className="footer-icon" />, text: companyAddress },
		{ icon: <PhoneIcon className="footer-icon" />, text: companyPhone, isPhone: true },
		{ icon: <EmailIcon className="footer-icon" />, text: companyEmail, isEmail: true },
	];

	return (
		<Box data-tour="main-page-footer">
			<Box className="footer-main" component="footer" pt={4} pb={1} px={2}>
				<Box display="flex" flexWrap="wrap" justifyContent="space-between" className="footer-container">
					{/* Logo and Description */}
					<Box flex="1 1 200px" mb={2} className="footer-logo-section">
						<Box display="flex" sx={{ justifyContent: "center" }} flexDirection="column" alignItems="start">
							<CompanyLogo
								variant="secondary"
								alt="Footer logo"
								className="footer-logo"
								skeletonWidth={120}
								skeletonHeight={40}
							/>
							<Typography variant="body2" className="footer-description">
								{t('UI_TEXT.COMPANY_MESSAGE', { company: companyName })}
							</Typography>
						</Box>
					</Box>

					{/* Contact Information */}
					<Box flex="1 1 200px" mb={2} className="footer-contact">
						<Typography variant="h6" className="footer-title">
							{t('UI_TEXT.CONTACT_US')}
						</Typography>
						{contactInfo.map((info, index) => (
							<Box key={index} display="flex" alignItems="center" className="footer-contact-item">
								{info.icon}
								{info.isEmail ? (
									<Link
										href={`mailto:${info.text}`}
										className="footer-link"
										color="inherit"
										variant="body2"
										fontSize={"12px"}
									>
										{info.text}
									</Link>
								) : info.isPhone ? (
									<Link
										href={`tel:${info.text}`}
										className="footer-link"
										color="inherit"
										variant="body2"
										fontSize={"12px"}
									>
										{info.text}
									</Link>
								) : (
									<Typography variant="body2" fontSize={"12px"}>{info.text}</Typography>
								)}
							</Box>
						))}
					</Box>

					{/* Main Navigation Links */}
					<Box flex="1 1 200px" mb={2} className="footer-links">
						<Typography variant="h6" className="footer-title">
							{t('UI_TEXT.QUICK_LINKS')}
						</Typography>
						<Box display="flex" flexDirection="column" className="footer-link-group">
							{quickLinks.map((link, index) => (
								<Link key={index} href={link.path} color="inherit" className="footer-link">
									{link.label}
								</Link>
							))}
						</Box>
					</Box>

					<Box flex="1 1 200px" mb={2} className="footer-links">
						<Typography variant="h6" className="footer-title">
							{t('LABELS.QUICK_ACTIONS')}
						</Typography>
						<Box display="flex" flexDirection="column" className="footer-link-group">
							{quickActions.map((link, index) => (
								<Link key={index} href={link.path} color="inherit" className="footer-link">
									{link.label}
								</Link>
							))}
						</Box>
					</Box>

					{/* Legal Links */}
					<Box flex="1 1 200px" mb={2} className="footer-legal-links">
						<Typography variant="h6" className="footer-title">
							{t('UI_TEXT.LEGAL')}
						</Typography>
						<Box display="flex" flexDirection="column" className="footer-link-group">
							{visibleLegalLinks.map((link, index) => (
								<Link
									key={index}
									component="button"
									type="button"
									onClick={() => handlePolicyClick(link)}
									color="inherit"
									className="footer-link"
									sx={{ textAlign: "left" }}
								>
									{link.label}
								</Link>
							))}
							{showFooterEula && (
								<Link
									component="button"
									type="button"
									onClick={handleEulaClick}
									color="inherit"
									className="footer-link"
									sx={{ textAlign: "left" }}
								>
									{t('LABELS.EULA')}
								</Link>
							)}
						</Box>
					</Box>
				</Box>

				{/* Divider and Copyright */}
				<Divider sx={{ mb: 2 }} variant="middle" />
				<Box textAlign="center" sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', columnGap: 1, mb: 1, flexWrap: 'wrap' }}>
					<Typography variant="body2" color="textSecondary">
						{t('FOOTER.ALL_RIGHTS_RESERVED', { year: new Date().getFullYear(), company: companyName })}
					</Typography>
					{(() => {
						const footerItems = [
							showFooterVersion && {
								id: "version",
								node: (
									<Link color="textSecondary" underline="hover" href={"/admin?tab=version_info"}>
										{t('LABELS.VERSION')}
									</Link>
								),
							},
							...visibleLegalLinks.map((item) => ({
								id: `legal-${item.id}`,
								node: (
									<Link
										component="button"
										type="button"
										color="textSecondary"
										underline="hover"
										onClick={() => handlePolicyClick(item)}
									>
										{item.label}
									</Link>
								),
							})),
							showFooterEula && {
								id: "eula",
								node: (
									<Link
										component="button"
										type="button"
										color="textSecondary"
										underline="hover"
										onClick={handleEulaClick}
									>
										{t('LABELS.EULA')}
									</Link>
								),
							},
							showFooterManual && {
								id: "manual",
								node: (
									<Link color="textSecondary" underline="hover" href={`${ASSETS_BASE_URL}/manual.pdf`}>
										{t('LABELS.MANUAL')}
									</Link>
								),
							},
							showFooterNeedHelp && {
								id: "needHelp",
								node: (
									<Link
										component="button"
										type="button"
										color="textSecondary"
										underline="hover"
										onClick={() => navigate(ROUTES.MARKDOWN)}
									>
										{t('LABELS.NEED_HELP')}
									</Link>
								),
							},
						].filter(Boolean);

						return footerItems.map((item, index) => (
							<React.Fragment key={item.id}>
								{index > 0 && (
									<Typography variant="body2" color="textSecondary">
										|
									</Typography>
								)}
								<Typography variant="body2" color="textSecondary">
									{item.node}
								</Typography>
							</React.Fragment>
						));
					})()}
					{showLanguageSelector && (
						<>
							<Typography variant="body2" color="textSecondary">|</Typography>
							<Language />
						</>
					)}
				</Box>
				<ComplianceDescriptionModal
					open={policyModal.open}
					setOpen={(open) =>
						setPolicyModal((prev) => ({ open, doc: open ? prev.doc : null }))
					}
					document={policyModal.doc}
				/>
				<EulaLibraryModal
					open={eulaModalOpen}
					setOpen={setEulaModalOpen}
					documents={eulaDocs}
				/>
			</Box>
		</Box>
	);
};

export default Footer;